import Logger from "../services/logger/Logger";
import WarranyFacade from "@robotical/appv2-warranty-service-lib";
import { DatabaseEnum, DatabaseManager, ServiceProgramDatabase } from "@robotical/analytics-gatherer";
import serviceProgramFirebaseConfig from "../database/config";
import { LoginToken, RegisteredUser } from "../types/generic";

const SHOW_LOGS = true;
const TAG = "OnboardingUtils";

export async function registerUser(email: string, establishment: string, serialNumbers: string[]): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Registering user with email: " + email);
    try {
        return WarranyFacade.registerUser(email, establishment, serialNumbers);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error registering user: " + error);
        return false;
    }
}

export async function getUserGivenEmail(email: string, onlyNewDb = false): Promise<RegisteredUser | null> {
    Logger.info(SHOW_LOGS, TAG, "Getting user given email. Looking in new db only?: " + onlyNewDb);
    // if there is no user with the email in the new database, check the legacy database
    const userInNewDb = await getUserGivenEmailNew(email);
    if (userInNewDb) {
        return userInNewDb;
    }
    if (onlyNewDb) {
        return null;
    }
    const userInLegacyDb = await getUserGivenEmailLegacy(email);
    if (userInLegacyDb) {
        return userInLegacyDb;
    }
    return null;
}

async function getUserGivenEmailNew(email: string): Promise<RegisteredUser | null> {
    Logger.info(SHOW_LOGS, TAG, "Getting user given email in the new db: " + email);
    try {
        const user = await WarranyFacade.getUser(email);
        return user;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting user given email: " + error);
        return null;
    }
}

async function getUserGivenEmailLegacy(email: string): Promise<RegisteredUser | null> {
    Logger.info(SHOW_LOGS, TAG, "Getting user given email in the legacy db: " + email);
    try {
        const dbManager = DatabaseManager.getInstance();
        const dbInstance = await dbManager.initializeOrGetDatabase(
            DatabaseEnum.SERVICE_PROGRAM, serviceProgramFirebaseConfig, DatabaseEnum.SERVICE_PROGRAM
        ) as ServiceProgramDatabase;
        const user = await dbInstance.getUser(email);
        return user;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting user given email: " + error);
        return null;
    }
}

export async function isUserRegistered(email: string, onlyNewDb = false): Promise<boolean> {
    // if there is no user with the email in the new database, check the legacy database
    const isRegisteredInNewDb = await isUserRegisteredNew(email);
    if (isRegisteredInNewDb) {
        return true;
    }
    if (onlyNewDb) {
        return false;
    }
    const isRegisteredInLegacyDb = await isUserRegisteredLegacy(email);
    if (isRegisteredInLegacyDb) {
        return true;
    }
    return false;
}

async function isUserRegisteredNew(email: string): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Checking if user is registered with email in the new db: " + email);
    try {
        return !!await WarranyFacade.getUser(email);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if user is registered: " + error);
        return false;
    }
}

async function isUserRegisteredLegacy(email: string): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Checking if user is registered with email in the legacy db: " + email);
    try {
        const dbManager = DatabaseManager.getInstance();
        const dbInstance = await dbManager.initializeOrGetDatabase(
            DatabaseEnum.SERVICE_PROGRAM, serviceProgramFirebaseConfig, DatabaseEnum.SERVICE_PROGRAM
        ) as ServiceProgramDatabase;
        return !!await dbInstance.getUser(email);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if user is registered: " + error);
        return false;
    }
}


export async function isSerialNumberRegistered(serialNo: string, onlyNewDb = false): Promise<boolean> {
    // if there is no user with the email in the new database, check the legacy database
    const isRegisteredInNewDb = await isSerialNumberRegisteredNew(serialNo);
    if (isRegisteredInNewDb) {
        return true;
    }
    if (onlyNewDb) {
        return false;
    }
    const isRegisteredInLegacyDb = await isSerialNumberRegisteredLegacy(serialNo);
    if (isRegisteredInLegacyDb) {
        return true;
    }
    return false;
}

const isSerialNumberRegisteredNew = async (serialNo: string): Promise<boolean> => {
    Logger.info(SHOW_LOGS, TAG, "Checking if serial number is registered in the new db: " + serialNo);
    try {
        return WarranyFacade.isSerialNumberRegistered(serialNo);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if serial number is registered: " + error);
        return false;
    }
};

const isSerialNumberRegisteredLegacy = async (serialNo: string): Promise<boolean> => {
    Logger.info(SHOW_LOGS, TAG, "Checking if serial number is registered in the legacy db: " + serialNo);
    try {
        const dbManager = DatabaseManager.getInstance();
        const dbInstance = await dbManager.initializeOrGetDatabase(
            DatabaseEnum.SERVICE_PROGRAM, serviceProgramFirebaseConfig, DatabaseEnum.SERVICE_PROGRAM
        ) as ServiceProgramDatabase;
        return !!await dbInstance.isSerialNumberRegistered(serialNo);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if serial number is registered: " + error);
        return false;
    }
}

export async function getLoginToken(email: string, onlyNewDb = false): Promise<LoginToken | undefined> {
        const loginTokenNew = await getLoginTokenNew(email);
        if (loginTokenNew) {
            return loginTokenNew;
        }
        if (onlyNewDb) {
            return undefined;
        }
        const loginTokenLegacy = await getLoginTokenLegacy(email);      
        if (loginTokenLegacy) {
            return loginTokenLegacy;
        }
        return undefined;
}

async function getLoginTokenNew(email: string): Promise<LoginToken | undefined> {
    Logger.info(SHOW_LOGS, TAG, "Getting login token in the new db: " + email);
    try {
        const token = await WarranyFacade.getLoginToken(email);
        return token;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting login token: " + error);
        return undefined;
    }
}
async function getLoginTokenLegacy(email: string): Promise<LoginToken | undefined> {
    Logger.info(SHOW_LOGS, TAG, "Getting login token in the legacy db: " + email);
    try {
        const dbManager = DatabaseManager.getInstance();
        const dbInstance = await dbManager.initializeOrGetDatabase(
            DatabaseEnum.SERVICE_PROGRAM, serviceProgramFirebaseConfig, DatabaseEnum.SERVICE_PROGRAM
        ) as ServiceProgramDatabase;
        const token = await dbInstance.getLoginToken(email);
        return token;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting login token: " + error);
        return undefined;
    }
}
